import { useEffect } from 'react';
import type { datadogLogs } from '@datadog/browser-logs';

import logMeasurementToDD from './logMeasurementToDD';

import mark from './mark';
import measure from './measure';
import { wasSpaNavigation } from './spaNavigationStart';

declare global {
  interface Window {
    // NOTE: it may be undefined at some point but if we declare the type with ?
    // typescript complains about mixed declarations for the same global property
    // (see that this code is repeated in many different places)
    datadogLogs: typeof datadogLogs;
  }
}

const logger = window?.datadogLogs?.createLogger('perceived-performance');

export default function useMeasureRenderFromNavigationStart(
  markName: string,
  onMeasure?: (measurement: {
    loadingType: 'initial_load' | 'route_change';
    renderTime: number;
  }) => void,
): void {
  useEffect(() => {
    const wasTheLastNavigationADynamicRouteChange = wasSpaNavigation();
    // NOTE: standard values from DD RUM API, for consistency
    const [navigationStartMark, loadingType] = wasTheLastNavigationADynamicRouteChange
      ? (['spaNavigationStart', 'route_change'] as const)
      : (['navigationStart', 'initial_load'] as const);

    // NOTE: the moment when the timeout callback scheduled inside the RAF
    // callback is called, is closer to when the user sees the actual content
    // rendered by the component on the screen
    // 1. component function is called
    // 2. `useEffect` hook runs, RAF callback is scheduled
    // 3. React modifies DOM (hopefully, we don't have any guarantees)
    // 4. RAF callback is called, timeout is scheduled
    // 5. The browser updates the screen
    // 6. The timeout callback is called, we report the render
    // There is a risk that something happens between 5 and 6, that delays the
    // measurement, for example a long task scheduled for before the timeout
    // callback here.
    // For better accuracy, we may consider using the `PerformanceElementTiming`
    // API, but it's not supported by all browsers and is quite difficult to use
    // as must be applied to certain kind of nodes
    let rafReference: ReturnType<typeof requestAnimationFrame> | null = null;
    let timeoutReference: ReturnType<typeof setTimeout> | null = null;

    rafReference = requestAnimationFrame(() => {
      timeoutReference = setTimeout(() => {
        mark(`render:${markName}`);
        // TODO: it works right now only for the first render
        // we should connect it to the router, same as the "measureAndLog", but
        // it'd be better to take the time of the last user interaction event
        // this is the actual moment when the navigation starts; when the router
        // kicks in, it's already too late, user might already wait unspecified
        // amout of time for the results of their actions (usually it's very
        // fast, but you cannot be sure
        const theMeasure = measure(
          `time-to-render:${markName}`,
          navigationStartMark,
          `render:${markName}`,
        );

        if (theMeasure) {
          logMeasurementToDD(theMeasure, { routeChange: wasTheLastNavigationADynamicRouteChange });

          logger?.info(`time-to-render:${markName}`, {
            value: theMeasure.duration,
            'view.loading_type': loadingType,
          });

          onMeasure?.({ loadingType, renderTime: theMeasure.duration });
        }
      });
    });

    return () => {
      if (rafReference !== null) {
        cancelAnimationFrame(rafReference);
      }

      if (timeoutReference !== null) {
        clearTimeout(timeoutReference);
      }
    };
  }, [markName, onMeasure]);
}
